import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Dropdown,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  TreeSelect,
  Typography,
  type TableProps,
} from 'antd';
import {
  DeleteOutlined,
  DollarOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  MoreOutlined,
  PlusOutlined,
  ProfileOutlined,
  ReloadOutlined,
  SortAscendingOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, FilterDrawer, ResponsiveList, useBreakpoint } from '@mall/shared';
import { useToast } from '../../app/ToastProvider';
import { listBrands } from '../brands/api';
import { listAllCategories } from '../categories/api';
import { categoryDisplayName, type CategoryView } from '../categories/types';
import {
  batchDeleteProducts,
  batchResetProductAttributes,
  batchUnpublishProducts,
  batchPublishProducts,
  batchUpdatePrices,
  batchUpdateSort,
  deleteProduct,
  exportProducts,
  listProducts,
  publishProduct,
  unpublishProduct,
} from './api';
import type { AttributeInput, ProductSnapshot } from './types';

const PRICE = (cents: number) => `¥ ${(cents / 100).toFixed(2)}`;
const STATUS_LABEL: Record<number, string> = {
  0: '草稿',
  1: '已发布',
  2: '已下架',
};
const STATUS_COLOR: Record<number, string> = {
  0: 'default',
  1: 'green',
  2: 'orange',
};

interface FilterFormValues {
  keyword?: string;
  categoryId?: number;
  brandId?: number;
  status?: number;
  originalPriceMinYuan?: number | null;
  originalPriceMaxYuan?: number | null;
}

interface BatchOriginalPriceFormValues {
  originalPriceYuan: number;
}

interface BatchSortFormValues {
  items: Array<{
    id: number;
    name: string;
    sortOrder: number;
  }>;
}

interface BatchAttributesFormValues {
  attributes: AttributeInput[];
}

function sanitizeAttributes(rows: AttributeInput[]): AttributeInput[] {
  return (rows ?? [])
    .map((a) => ({
      attrKey: (a.attrKey ?? '').trim(),
      attrKeyEn: a.attrKeyEn?.trim() || null,
      options: (a.options ?? [])
        .map((o) => {
          const zh = (o.zh ?? '').trim();
          const en = (o.en ?? '').trim();
          return { zh, en: en || zh };
        })
        .filter((o) => o.zh.length > 0),
      sortOrder: a.sortOrder ?? 0,
    }))
    .filter((a) => a.attrKey.length > 0 && a.options.length > 0);
}

interface CategoryTreeOption {
  title: string;
  value: number;
  children: CategoryTreeOption[];
}

function buildCategoryOptions(rows: CategoryView[]): CategoryTreeOption[] {
  const nodes = new Map<number, CategoryTreeOption>();
  for (const row of rows) {
    nodes.set(row.id, { title: categoryDisplayName(row), value: row.id, children: [] });
  }
  const roots: CategoryTreeOption[] = [];
  for (const row of rows) {
    const node = nodes.get(row.id);
    if (!node) continue;
    if (row.parentId && nodes.has(row.parentId)) {
      nodes.get(row.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

function parseImages(record: Pick<ProductSnapshot, 'mainImage' | 'images'> | null): string[] {
  if (!record) return [];
  const urls: string[] = [];
  if (record.mainImage) {
    urls.push(record.mainImage);
  }
  if (record.images) {
    try {
      const parsed = JSON.parse(record.images) as unknown;
      if (Array.isArray(parsed)) {
        for (const item of parsed) {
          if (typeof item === 'string' && item) {
            urls.push(item);
          }
        }
      }
    } catch {
      // The backend normally returns a JSON array string. Ignore malformed legacy values.
    }
  }
  return Array.from(new Set(urls));
}

function yuanToCents(value: number | null | undefined): number | undefined {
  return value == null ? undefined : Math.round(value * 100);
}

function isPublished(record: ProductSnapshot): boolean {
  return record.status === 1;
}

export function ProductsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const pageSize = Math.min(5000, Math.max(1, Number(searchParams.get('page_size')) || 20));
  const filters = useMemo<FilterFormValues>(
    () => ({
      keyword: searchParams.get('keyword') || undefined,
      categoryId: searchParams.get('category_id') ? Number(searchParams.get('category_id')) : undefined,
      brandId: searchParams.get('brand_id') ? Number(searchParams.get('brand_id')) : undefined,
      status: searchParams.get('status') != null ? Number(searchParams.get('status')) : undefined,
      originalPriceMinYuan: searchParams.get('op_min') != null ? Number(searchParams.get('op_min')) : undefined,
      originalPriceMaxYuan: searchParams.get('op_max') != null ? Number(searchParams.get('op_max')) : undefined,
    }),
    [searchParams],
  );
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
  const [batchOriginalPriceOpen, setBatchOriginalPriceOpen] = useState(false);
  const [batchSortOpen, setBatchSortOpen] = useState(false);
  const [batchSortStart, setBatchSortStart] = useState(0);
  const [batchAttributesOpen, setBatchAttributesOpen] = useState(false);
  const [imageRecord, setImageRecord] = useState<ProductSnapshot | null>(null);
  const [exporting, setExporting] = useState(false);
  const [filterForm] = Form.useForm<FilterFormValues>();
  const [batchOriginalPriceForm] = Form.useForm<BatchOriginalPriceFormValues>();
  const [batchSortForm] = Form.useForm<BatchSortFormValues>();
  const [batchAttributesForm] = Form.useForm<BatchAttributesFormValues>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'mobile';
  const activeFilterCount = useMemo(
    () => Object.values(filters).filter((v) => v != null && v !== '').length,
    [filters],
  );

  const setPagination = (nextPage: number, nextPageSize: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(nextPage));
      next.set('page_size', String(nextPageSize));
      return next;
    });
  };

  const query = useQuery({
    queryKey: ['admin', 'products', page, pageSize, filters],
    queryFn: () =>
      listProducts({
        page,
        pageSize,
        keyword: filters.keyword?.trim(),
        categoryId: filters.categoryId,
        brandId: filters.brandId,
        status: filters.status,
        originalPriceMinCents: yuanToCents(filters.originalPriceMinYuan),
        originalPriceMaxCents: yuanToCents(filters.originalPriceMaxYuan),
      }),
  });
  const categoriesQuery = useQuery({ queryKey: ['admin', 'categories', 'all'], queryFn: listAllCategories });
  const brandsQuery = useQuery({ queryKey: ['admin', 'brands'], queryFn: listBrands });
  const categoryOptions = useMemo(
    () => buildCategoryOptions(categoriesQuery.data ?? []),
    [categoriesQuery.data],
  );
  const categoryMap = useMemo(
    () => new Map((categoriesQuery.data ?? []).map((category) => [category.id, category])),
    [categoriesQuery.data],
  );
  const brandMap = useMemo(
    () => new Map((brandsQuery.data ?? []).map((brand) => [brand.id, brand])),
    [brandsQuery.data],
  );
  const selectedProducts = useMemo(() => {
    const idSet = new Set(selectedProductIds);
    return (query.data?.list ?? []).filter((product) => idSet.has(product.id));
  }, [query.data?.list, selectedProductIds]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
  };

  const publishMutation = useMutation({
    mutationFn: (record: ProductSnapshot) =>
      isPublished(record) ? unpublishProduct(record.id) : publishProduct(record.id),
    onSuccess: () => {
      toast.success('已更新上架状态');
      invalidate();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '操作失败');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteProduct(id),
    onSuccess: () => {
      toast.success('已删除');
      invalidate();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '删除失败');
    },
  });

  const batchOriginalPriceMutation = useMutation({
    mutationFn: (values: BatchOriginalPriceFormValues) =>
      batchUpdatePrices({
        ids: selectedProductIds,
        originalPriceCents: yuanToCents(values.originalPriceYuan),
      }),
    onSuccess: (res) => {
      toast.success(`已更新 ${res.updated} 个商品原价`);
      setBatchOriginalPriceOpen(false);
      setSelectedProductIds([]);
      batchOriginalPriceForm.resetFields();
      invalidate();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '批量改原价失败');
    },
  });

  const batchUnpublishMutation = useMutation({
    mutationFn: () => batchUnpublishProducts(selectedProductIds),
    onSuccess: (res) => {
      toast.success(`已下架 ${res.updated} 个商品`);
      setSelectedProductIds([]);
      invalidate();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '批量下架失败');
    },
  });

  const batchPublishMutation = useMutation({
    mutationFn: () => batchPublishProducts(selectedProductIds),
    onSuccess: (res) => {
      toast.success(`已上架 ${res.updated} 个商品`);
      setSelectedProductIds([]);
      invalidate();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '批量上架失败');
    },
  });

  const batchDeleteMutation = useMutation({
    mutationFn: () => batchDeleteProducts(selectedProductIds),
    onSuccess: (res) => {
      toast.success(`已删除 ${res.updated} 个商品`);
      setSelectedProductIds([]);
      invalidate();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '批量删除失败');
    },
  });

  const batchSortMutation = useMutation({
    mutationFn: (values: BatchSortFormValues) =>
      batchUpdateSort({
        items: values.items.map((item) => ({
          id: item.id,
          sortOrder: item.sortOrder ?? 0,
        })),
      }),
    onSuccess: (res) => {
      toast.success(`已更新 ${res.updated} 个商品排序`);
      setBatchSortOpen(false);
      setSelectedProductIds([]);
      batchSortForm.resetFields();
      invalidate();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '批量排序失败');
    },
  });

  const batchAttributesMutation = useMutation({
    mutationFn: (values: BatchAttributesFormValues) =>
      batchResetProductAttributes({
        ids: selectedProductIds,
        attributes: sanitizeAttributes(values.attributes ?? []),
      }),
    onSuccess: (res) => {
      toast.success(`已重置 ${res.updated} 个商品的属性`);
      setBatchAttributesOpen(false);
      setSelectedProductIds([]);
      batchAttributesForm.resetFields();
      invalidate();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '批量重置属性失败');
    },
  });

  const openBatchAttributesModal = () => {
    batchAttributesForm.setFieldsValue({
      attributes: [{ attrKey: '', attrKeyEn: '', options: [{ zh: '', en: '' }], sortOrder: 0 }],
    });
    setBatchAttributesOpen(true);
  };

  const openBatchSortModal = () => {
    setBatchSortStart(0);
    batchSortForm.setFieldsValue({
      items: selectedProducts.map((product) => ({
        id: product.id,
        name: product.name,
        sortOrder: product.sortOrder ?? 0,
      })),
    });
    setBatchSortOpen(true);
  };

  const applyIncrementalSortOrders = () => {
    const items = batchSortForm.getFieldValue('items') as BatchSortFormValues['items'] | undefined;
    if (!items?.length) return;
    batchSortForm.setFieldsValue({
      items: items.map((item, index) => ({
        ...item,
        sortOrder: batchSortStart + index,
      })),
    });
  };

  useEffect(() => {
    filterForm.setFieldsValue({
      keyword: filters.keyword,
      categoryId: filters.categoryId,
      brandId: filters.brandId,
      status: filters.status,
      originalPriceMinYuan: filters.originalPriceMinYuan,
      originalPriceMaxYuan: filters.originalPriceMaxYuan,
    });
  }, [filterForm, filters]);

  const applyFilters = (values: FilterFormValues) => {
    setSelectedProductIds([]);
    setSearchParams(() => {
      const next = new URLSearchParams();
      if (values.keyword?.trim()) next.set('keyword', values.keyword.trim());
      if (values.categoryId != null) next.set('category_id', String(values.categoryId));
      if (values.brandId != null) next.set('brand_id', String(values.brandId));
      if (values.status != null) next.set('status', String(values.status));
      if (values.originalPriceMinYuan != null) next.set('op_min', String(values.originalPriceMinYuan));
      if (values.originalPriceMaxYuan != null) next.set('op_max', String(values.originalPriceMaxYuan));
      next.set('page', '1');
      next.set('page_size', String(pageSize));
      return next;
    });
  };

  const resetFilters = () => {
    filterForm.resetFields();
    setSelectedProductIds([]);
    setSearchParams(new URLSearchParams());
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportProducts({
        keyword: filters.keyword?.trim(),
        categoryId: filters.categoryId,
        brandId: filters.brandId,
        status: filters.status,
        originalPriceMinCents: yuanToCents(filters.originalPriceMinYuan),
        originalPriceMaxCents: yuanToCents(filters.originalPriceMaxYuan),
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14);
      anchor.href = url;
      anchor.download = `商品导出-${stamp}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success('导出已开始下载（Excel，含主图，不含售价）');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : '导出失败');
    } finally {
      setExporting(false);
    }
  };

  const columns: TableProps<ProductSnapshot>['columns'] = [
    {
      title: '主图',
      dataIndex: 'mainImage',
      width: 80,
      fixed: 'left',
      render: (_: string | null, record) => {
        const images = parseImages(record);
        if (images.length === 0) {
          return <span style={{ color: '#bfbfbf' }}>—</span>;
        }
        return (
          <Image
            src={images[0]}
            width={48}
            height={48}
            preview={false}
            style={{ objectFit: 'cover', cursor: 'pointer' }}
            onClick={() => setImageRecord(record)}
          />
        );
      },
    },
    {
      title: '商品编码',
      dataIndex: 'productCode',
      width: 150,
      render: (code: string) => <code className="mall-row-code">{code}</code>,
    },
    {
      title: '名称',
      dataIndex: 'name',
      width: 300,
      render: (name: string, record) => (
        <div>
          <Space size={8} align="center" wrap>
            <Link className="mall-row-title" to={`/products/${record.id}`} state={{ from: `/products${searchParams.toString() ? `?${searchParams.toString()}` : ''}` }}>
              {name}
            </Link>
            {record.originalPriceCents != null && (
              <span style={{ color: '#cf1322', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {PRICE(record.originalPriceCents)}
              </span>
            )}
          </Space>
          <div className="mall-row-subtitle">{record.nameEn || '未维护英文名'}</div>
        </div>
      ),
    },
    {
      title: '分类 / 品牌',
      key: 'categoryBrand',
      width: 220,
      render: (_, record) => {
        const category = record.categoryId ? categoryMap.get(record.categoryId) : undefined;
        const brand = record.brandId ? brandMap.get(record.brandId) : undefined;
        return (
          <Space direction="vertical" size={0}>
            <span>{category ? categoryDisplayName(category) : '未分类'}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {brand?.name ?? '无品牌'}
            </Typography.Text>
          </Space>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status: number | null | undefined) => {
        const value = status ?? 0;
        return (
          <Tag color={STATUS_COLOR[value] ?? 'default'}>
            {STATUS_LABEL[value] ?? `未知(${value})`}
          </Tag>
        );
      },
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      width: 80,
      align: 'right',
      render: (sortOrder: number | null | undefined) => sortOrder ?? 0,
    },
    {
      title: '属性',
      dataIndex: 'attributes',
      width: 260,
      render: (attrs: ProductSnapshot['attributes']) =>
        attrs.length === 0 ? (
          <span style={{ color: '#bfbfbf' }}>—</span>
        ) : (
          <Space size={[4, 4]} wrap>
            {attrs.slice(0, 3).map((a) => (
              <Tag key={`${a.id ?? a.attrKey}`}>
                {a.attrKey}:{' '}
                {(a.options ?? [])
                  .map((o) => o.zh)
                  .filter(Boolean)
                  .join('、') || '—'}
              </Tag>
            ))}
            {attrs.length > 3 ? <Tag>+{attrs.length - 3}</Tag> : null}
          </Space>
        ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 240,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => navigate(`/products/${record.id}`, { state: { from: `/products${searchParams.toString() ? `?${searchParams.toString()}` : ''}` } })}
          >
            编辑
          </Button>
          <Button
            size="small"
            icon={isPublished(record) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            loading={publishMutation.isPending && publishMutation.variables?.id === record.id}
            onClick={() => publishMutation.mutate(record)}
          >
            {isPublished(record) ? '下架' : '上架'}
          </Button>
          <Popconfirm
            title={`确认删除「${record.name}」？`}
            description="删除后商品将从列表中移除，此操作不可撤销。"
            okText="删除"
            okButtonProps={{ danger: true }}
            cancelText="取消"
            onConfirm={() => deleteMutation.mutate(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="mall-page">
      <div className="mall-page-header">
        <div>
          <div className="mall-page-kicker">Products</div>
          <Typography.Title level={3} className="mall-page-title">
            商品管理
          </Typography.Title>
          <p className="mall-page-description">
            维护商品基础信息、上下架状态和商户可见的原价策略。
          </p>
        </div>
        {isMobile ? (
          <Space>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'export',
                    icon: <DownloadOutlined />,
                    label: '导出',
                    onClick: () => void handleExport(),
                  },
                  {
                    key: 'refresh',
                    icon: <ReloadOutlined />,
                    label: '刷新',
                    onClick: () => invalidate(),
                  },
                ],
              }}
            >
              <Button icon={<MoreOutlined />}>更多</Button>
            </Dropdown>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/products/new')}>
              新建
            </Button>
          </Space>
        ) : (
          <Space wrap>
            <Button
              icon={<SortAscendingOutlined />}
              disabled={selectedProductIds.length === 0}
              onClick={openBatchSortModal}
            >
              批量排序{selectedProductIds.length > 0 ? ` (${selectedProductIds.length})` : ''}
            </Button>
            <Button
              icon={<DollarOutlined />}
              disabled={selectedProductIds.length === 0}
              onClick={() => {
                batchOriginalPriceForm.resetFields();
                setBatchOriginalPriceOpen(true);
              }}
            >
              批量改原价{selectedProductIds.length > 0 ? ` (${selectedProductIds.length})` : ''}
            </Button>
            <Button
              icon={<ProfileOutlined />}
              disabled={selectedProductIds.length === 0}
              onClick={openBatchAttributesModal}
            >
              批量重置属性{selectedProductIds.length > 0 ? ` (${selectedProductIds.length})` : ''}
            </Button>
            <Popconfirm
              title={`确认上架选中的 ${selectedProductIds.length} 个商品？`}
              okText="上架"
              cancelText="取消"
              disabled={selectedProductIds.length === 0}
              onConfirm={() => batchPublishMutation.mutate()}
            >
              <Button
                icon={<EyeOutlined />}
                disabled={selectedProductIds.length === 0}
                loading={batchPublishMutation.isPending}
              >
                批量上架{selectedProductIds.length > 0 ? ` (${selectedProductIds.length})` : ''}
              </Button>
            </Popconfirm>
            <Popconfirm
              title={`确认下架选中的 ${selectedProductIds.length} 个商品？`}
              okText="下架"
              cancelText="取消"
              disabled={selectedProductIds.length === 0}
              onConfirm={() => batchUnpublishMutation.mutate()}
            >
              <Button
                icon={<EyeInvisibleOutlined />}
                disabled={selectedProductIds.length === 0}
                loading={batchUnpublishMutation.isPending}
              >
                批量下架{selectedProductIds.length > 0 ? ` (${selectedProductIds.length})` : ''}
              </Button>
            </Popconfirm>
            <Popconfirm
              title={`确认删除选中的 ${selectedProductIds.length} 个商品？`}
              description="删除后商品将从列表中移除，此操作不可撤销。"
              okText="删除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              disabled={selectedProductIds.length === 0}
              onConfirm={() => batchDeleteMutation.mutate()}
            >
              <span>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  disabled={selectedProductIds.length === 0}
                  loading={batchDeleteMutation.isPending}
                >
                  批量删除{selectedProductIds.length > 0 ? ` (${selectedProductIds.length})` : ''}
                </Button>
              </span>
            </Popconfirm>
            <Button icon={<DownloadOutlined />} onClick={handleExport} loading={exporting}>
              导出
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => invalidate()}
              loading={query.isFetching}
            >
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/products/new')}>
              新建商品
            </Button>
          </Space>
        )}
      </div>
      <FilterDrawer
        activeCount={activeFilterCount}
        onSubmit={() => filterForm.submit()}
        onReset={resetFilters}
      >
        <Form<FilterFormValues> form={filterForm} layout="vertical" onFinish={applyFilters}>
          <Row gutter={12}>
            <Col xs={24} md={8} lg={6}>
              <Form.Item label="关键词" name="keyword">
                <Input
                  allowClear
                  placeholder="名称 / 编码"
                  onPressEnter={() => filterForm.submit()}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Form.Item label="分类" name="categoryId">
                <TreeSelect
                  allowClear
                  showSearch
                  treeDefaultExpandAll={false}
                  treeData={categoryOptions}
                  placeholder="全部分类"
                  loading={categoriesQuery.isLoading}
                  treeNodeFilterProp="title"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Form.Item label="品牌" name="brandId">
                <Select
                  allowClear
                  showSearch
                  placeholder="全部品牌"
                  loading={brandsQuery.isLoading}
                  optionFilterProp="label"
                  options={(brandsQuery.data ?? []).map((brand) => ({
                    label: brand.name,
                    value: brand.id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Form.Item label="状态" name="status">
                <Select
                  allowClear
                  placeholder="全部状态"
                  options={[
                    { value: 0, label: STATUS_LABEL[0] },
                    { value: 1, label: STATUS_LABEL[1] },
                    { value: 2, label: STATUS_LABEL[2] },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6}>
              <Form.Item label="原价区间（元）">
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="originalPriceMinYuan" noStyle>
                    <InputNumber
                      min={0}
                      precision={2}
                      placeholder="最低"
                      style={{ width: '50%' }}
                    />
                  </Form.Item>
                  <Form.Item name="originalPriceMaxYuan" noStyle>
                    <InputNumber
                      min={0}
                      precision={2}
                      placeholder="最高"
                      style={{ width: '50%' }}
                    />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </Col>
            {!isMobile && (
              <Col xs={24} md={12} lg={6} style={{ display: 'flex', alignItems: 'end' }}>
                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit">
                      查询
                    </Button>
                    <Button onClick={resetFilters}>重置</Button>
                  </Space>
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </FilterDrawer>
      <div className="mall-table-card">
        <div className="mall-table-toolbar">
          <div>
            <div className="mall-table-title">商品列表</div>
            <div className="mall-table-subtitle">
              共 {query.data?.total ?? 0} 个商品
              {selectedProductIds.length > 0 ? `，已选择 ${selectedProductIds.length} 个` : ''}
            </div>
          </div>
          <div className="mall-table-actions">
            <Button
              size="small"
              disabled={selectedProductIds.length === 0}
              onClick={() => setSelectedProductIds([])}
            >
              清空选择
            </Button>
          </div>
        </div>
        <ResponsiveList<ProductSnapshot>
          rowKey={(record) => record.id}
          loading={query.isLoading}
          dataSource={query.data?.list ?? []}
          selectedRowKeys={selectedProductIds}
          onSelectChange={(keys) => setSelectedProductIds(keys.map((key) => Number(key)))}
          emptyText="暂无商品"
          pagination={{
            current: page,
            pageSize,
            total: query.data?.total ?? 0,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / 共 ${total} 条`,
            pageSizeOptions: [10, 20, 50, 100, 200, 500, 1000, 2000, 5000],
            onChange: (p, size) => setPagination(p, size),
          }}
          tableProps={{
            size: 'small',
            columns,
            scroll: { x: 1200 },
            rowSelection: {
              selectedRowKeys: selectedProductIds,
              onChange: (keys) => setSelectedProductIds(keys.map((key) => Number(key))),
            },
            locale: { emptyText: '暂无商品' },
          }}
          renderCard={(record) => {
            const images = parseImages(record);
            const category = record.categoryId ? categoryMap.get(record.categoryId) : undefined;
            const brand = record.brandId ? brandMap.get(record.brandId) : undefined;
            const status = record.status ?? 0;
            return (
              <div>
                <div style={{ display: 'flex', gap: 12 }}>
                  {images.length > 0 ? (
                    <Image
                      src={images[0]}
                      width={56}
                      height={56}
                      preview={false}
                      style={{ objectFit: 'cover', borderRadius: 8, cursor: 'pointer', flex: 'none' }}
                      onClick={() => setImageRecord(record)}
                    />
                  ) : (
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 8,
                        background: '#f1f5f9',
                        color: '#bfbfbf',
                        display: 'grid',
                        placeItems: 'center',
                        flex: 'none',
                      }}
                    >
                      —
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Link className="mall-row-title" to={`/products/${record.id}`} state={{ from: `/products${searchParams.toString() ? `?${searchParams.toString()}` : ''}` }}>
                      {record.name}
                    </Link>
                    <div className="mall-row-subtitle">{record.productCode}</div>
                    <div
                      style={{
                        marginTop: 4,
                        display: 'flex',
                        gap: 6,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                      }}
                    >
                      <Tag color={STATUS_COLOR[status] ?? 'default'}>
                        {STATUS_LABEL[status] ?? `未知(${status})`}
                      </Tag>
                      <span style={{ fontSize: 12, color: '#64748b' }}>
                        {category ? categoryDisplayName(category) : '未分类'} · {brand?.name ?? '无品牌'}
                      </span>
                    </div>
                    <div style={{ marginTop: 4, fontWeight: 600 }}>
                      {record.originalPriceCents == null ? '—' : PRICE(record.originalPriceCents)}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => navigate(`/products/${record.id}`, { state: { from: `/products${searchParams.toString() ? `?${searchParams.toString()}` : ''}` } })}
                  >
                    编辑
                  </Button>
                  <Button
                    size="small"
                    icon={isPublished(record) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    loading={publishMutation.isPending && publishMutation.variables?.id === record.id}
                    onClick={() => publishMutation.mutate(record)}
                  >
                    {isPublished(record) ? '下架' : '上架'}
                  </Button>
                  <Popconfirm
                    title={`确认删除「${record.name}」？`}
                    okText="删除"
                    okButtonProps={{ danger: true }}
                    cancelText="取消"
                    onConfirm={() => deleteMutation.mutate(record.id)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />}>
                      删除
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            );
          }}
        />
      </div>
      {isMobile && selectedProductIds.length > 0 && (
        <div className="mall-batch-bar">
          <span className="mall-batch-bar-count">已选 {selectedProductIds.length} 项</span>
          <Space size={8} wrap>
            <Button size="small" icon={<SortAscendingOutlined />} onClick={openBatchSortModal}>
              排序
            </Button>
            <Button
              size="small"
              icon={<DollarOutlined />}
              onClick={() => {
                batchOriginalPriceForm.resetFields();
                setBatchOriginalPriceOpen(true);
              }}
            >
              改原价
            </Button>
            <Button size="small" icon={<ProfileOutlined />} onClick={openBatchAttributesModal}>
              重置属性
            </Button>
            <Popconfirm
              title={`确认上架选中的 ${selectedProductIds.length} 个商品？`}
              okText="上架"
              cancelText="取消"
              onConfirm={() => batchPublishMutation.mutate()}
            >
              <Button size="small" icon={<EyeOutlined />} loading={batchPublishMutation.isPending}>
                上架
              </Button>
            </Popconfirm>
            <Popconfirm
              title={`确认下架选中的 ${selectedProductIds.length} 个商品？`}
              okText="下架"
              cancelText="取消"
              onConfirm={() => batchUnpublishMutation.mutate()}
            >
              <Button size="small" icon={<EyeInvisibleOutlined />} loading={batchUnpublishMutation.isPending}>
                下架
              </Button>
            </Popconfirm>
            <Popconfirm
              title={`确认删除选中的 ${selectedProductIds.length} 个商品？`}
              okText="删除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              onConfirm={() => batchDeleteMutation.mutate()}
            >
              <Button size="small" danger icon={<DeleteOutlined />} loading={batchDeleteMutation.isPending}>
                删除
              </Button>
            </Popconfirm>
            <Button size="small" type="text" onClick={() => setSelectedProductIds([])}>
              取消
            </Button>
          </Space>
        </div>
      )}
      <Modal
        title={imageRecord ? `${imageRecord.name} 的全部图片` : '商品图片'}
        open={imageRecord != null}
        footer={null}
        width={isMobile ? '94vw' : 820}
        onCancel={() => setImageRecord(null)}
        destroyOnClose
      >
        <Image.PreviewGroup>
          <Space size={[8, 8]} wrap>
            {parseImages(imageRecord).map((src) => (
              <Image key={src} src={src} width={120} height={120} style={{ objectFit: 'cover' }} />
            ))}
          </Space>
        </Image.PreviewGroup>
      </Modal>
      <Modal
        title={`批量修改原价（${selectedProductIds.length} 个商品）`}
        open={batchOriginalPriceOpen}
        onCancel={() => setBatchOriginalPriceOpen(false)}
        onOk={() => batchOriginalPriceForm.submit()}
        confirmLoading={batchOriginalPriceMutation.isPending}
        destroyOnClose
      >
        <Form<BatchOriginalPriceFormValues>
          form={batchOriginalPriceForm}
          layout="vertical"
          onFinish={(values) => batchOriginalPriceMutation.mutate(values)}
        >
          <Form.Item
            label="原价（元）"
            name="originalPriceYuan"
            rules={[{ required: true, message: '请输入原价' }]}
          >
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={`批量排序（${selectedProductIds.length} 个商品）`}
        open={batchSortOpen}
        onCancel={() => setBatchSortOpen(false)}
        onOk={() => batchSortForm.submit()}
        confirmLoading={batchSortMutation.isPending}
        destroyOnClose
        width={isMobile ? '94vw' : 720}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          数值越小越靠前；相同排序值时，最近更新的商品排在前面。
        </Typography.Paragraph>
        <Space style={{ marginBottom: 16 }}>
          <span>起始排序：</span>
          <InputNumber
            min={0}
            precision={0}
            value={batchSortStart}
            onChange={(value) => setBatchSortStart(value ?? 0)}
          />
          <Button onClick={applyIncrementalSortOrders}>按列表顺序递增</Button>
        </Space>
        <Form<BatchSortFormValues>
          form={batchSortForm}
          layout="vertical"
          onFinish={(values) => batchSortMutation.mutate(values)}
        >
          <Form.List name="items">
            {(fields) => (
              <Table
                size="small"
                pagination={false}
                rowKey="key"
                dataSource={fields}
                columns={[
                  {
                    title: '商品',
                    render: (_, field) => (
                      <>
                        <Form.Item name={[field.name, 'id']} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item name={[field.name, 'name']} noStyle>
                          <Input bordered={false} readOnly />
                        </Form.Item>
                      </>
                    ),
                  },
                  {
                    title: '排序',
                    width: 140,
                    render: (_, field) => (
                      <Form.Item
                        name={[field.name, 'sortOrder']}
                        rules={[{ required: true, message: '请输入排序' }]}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                      </Form.Item>
                    ),
                  },
                ]}
              />
            )}
          </Form.List>
        </Form>
      </Modal>
      <Modal
        title={`批量重置属性（${selectedProductIds.length} 个商品）`}
        open={batchAttributesOpen}
        onCancel={() => setBatchAttributesOpen(false)}
        onOk={() => batchAttributesForm.submit()}
        okText="清空旧属性并保存"
        confirmLoading={batchAttributesMutation.isPending}
        destroyOnClose
        width={isMobile ? '94vw' : 720}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="选中商品的现有属性将被全部清空，并替换为下方设置的属性。"
          description="此操作不可撤销，请至少设置一个属性。"
        />
        <Form<BatchAttributesFormValues>
          form={batchAttributesForm}
          layout="vertical"
          onFinish={(values) => {
            const attrs = sanitizeAttributes(values.attributes ?? []);
            if (attrs.length === 0) {
              toast.error('请至少设置一个有效属性');
              return;
            }
            Modal.confirm({
              title: `确认重置 ${selectedProductIds.length} 个商品的属性？`,
              content: `将清空选中商品的现有属性，并替换为 ${attrs.length} 个新属性，此操作不可撤销。`,
              okText: '确认重置',
              okButtonProps: { danger: true },
              cancelText: '取消',
              onOk: () => batchAttributesMutation.mutateAsync(values),
            });
          }}
        >
          <Form.List name="attributes">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <div
                    key={field.key}
                    style={{
                      border: '1px dashed #d9d9d9',
                      padding: 12,
                      borderRadius: 4,
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 8,
                      }}
                    >
                      <Tag color="blue">属性 #{index + 1}</Tag>
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                      >
                        移除
                      </Button>
                    </div>
                    <Row gutter={8}>
                      <Col xs={24} md={8}>
                        <Form.Item
                          label="维度名称（中文）"
                          {...field}
                          name={[field.name, 'attrKey']}
                          rules={[{ required: true, message: '必填' }]}
                        >
                          <Input placeholder="例如：尺寸、颜色" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item label="维度名称（英文）" {...field} name={[field.name, 'attrKeyEn']}>
                          <Input placeholder="e.g. Size, Color" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item label="排序" {...field} name={[field.name, 'sortOrder']}>
                          <InputNumber step={1} min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      可选取值（每条 = 中文 + 英文）
                    </Typography.Text>
                    <Form.List name={[field.name, 'options']}>
                      {(optFields, { add: addOpt, remove: removeOpt }) => (
                        <>
                          {optFields.map((optField) => (
                            <Row
                              key={optField.key}
                              gutter={8}
                              align="middle"
                              style={{ marginBottom: 8 }}
                            >
                              <Col xs={24} md={10}>
                                <Form.Item
                                  {...optField}
                                  name={[optField.name, 'zh']}
                                  rules={[{ required: true, message: '中文取值必填' }]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input placeholder="中文，如 L、红" />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={10}>
                                <Form.Item
                                  {...optField}
                                  name={[optField.name, 'en']}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input placeholder="英文，如 L、Red（可空，空则沿用中文）" />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={4}>
                                <Button
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeOpt(optField.name)}
                                >
                                  删
                                </Button>
                              </Col>
                            </Row>
                          ))}
                          <Button
                            type="dashed"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => addOpt({ zh: '', en: '' })}
                            style={{ marginBottom: 8 }}
                          >
                            添加一条取值
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </div>
                ))}
                <Button
                  block
                  icon={<PlusOutlined />}
                  onClick={() =>
                    add({ attrKey: '', attrKeyEn: '', options: [{ zh: '', en: '' }], sortOrder: 0 })
                  }
                >
                  添加属性
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
      {query.isError && (
        <Typography.Paragraph type="danger" style={{ marginTop: 16 }}>
          列表加载失败：{query.error instanceof Error ? query.error.message : '未知错误'}
        </Typography.Paragraph>
      )}
    </div>
  );
}
